import { useState } from 'react';
import {
  Button,
  Checkbox,
  PasswordInput,
  RadioGroup,
  Textarea,
  TextInput,
  ThemeToggle,
  Toggle,
} from '@fluent/ui';
import './Cluster1Review.css';

/**
 * Phase 1 Cluster 1 — Inputs review page.
 * 6 primitives: TextInput, PasswordInput, Textarea, Checkbox, RadioGroup, Toggle.
 * All tokens-driven, light + dark, keyboard accessible, 44px touch targets,
 * Georgian-labeled, forwarded refs + native props.
 */
export function Cluster1Review() {
  const [email, setEmail] = useState('');
  const [bio, setBio] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [agreed, setAgreed] = useState(false);
  const [level, setLevel] = useState<'beginner' | 'some'>('beginner');
  const [goal, setGoal] = useState<'5' | '10' | '15' | '20'>('10');
  const [sound, setSound] = useState(true);
  const [haptics, setHaptics] = useState(true);

  const validateEmail = () => {
    if (!email) return setEmailError('ელფოსტა აუცილებელია');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setEmailError('არასწორი ელფოსტის ფორმატი');
    setEmailError(undefined);
  };

  return (
    <main className="c1">
      <header className="c1__hero">
        <p className="c1__kicker">Phase 1 · Cluster 1</p>
        <h1>Inputs</h1>
        <p className="c1__lede">6 primitives. Type, tap, toggle. Everything keyboard-accessible, both themes.</p>
        <div className="c1__theme-row">
          <ThemeToggle />
        </div>
      </header>

      {/* Text inputs */}
      <section className="c1__section">
        <h2>TextInput · PasswordInput · Textarea</h2>
        <p className="c1__note">Same frame, same focus ring, same error state.</p>

        <div className="c1__stack">
          <TextInput
            label="ელფოსტა"
            type="email"
            placeholder="you@mail.ge"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={validateEmail}
            error={emailError}
            hint="შენს ელფოსტას არავის გადავცემთ."
            required
            autoComplete="email"
          />

          <PasswordInput
            label="პაროლი"
            placeholder="მინიმუმ 8 სიმბოლო"
            hint="რაც უფრო გრძელია, მით უფრო უსაფრთხოა."
            required
          />

          <Textarea
            label="მოგვიყევი შენს შესახებ"
            placeholder="რა გიზიდავს ინგლისურში?"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={200}
            showCount
            rows={3}
          />

          <TextInput
            label="გამორთული ველი"
            defaultValue="ცვლილება არ შეიძლება"
            disabled
          />
        </div>
      </section>

      {/* Selection */}
      <section className="c1__section">
        <h2>Checkbox · RadioGroup</h2>
        <p className="c1__note">Custom paint over the native input — full keyboard + screen reader support.</p>

        <div className="c1__stack">
          <Checkbox
            label="ვეთანხმები წესებსა და პირობებს"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />

          <Checkbox label="გამორთული არჩევანი" disabled />

          <RadioGroup
            legend="დონე"
            options={[
              { value: 'beginner', label: 'ინგლისური არასდროს მისწავლია' },
              { value: 'some', label: 'ცოტა ვიცი' },
            ]}
            value={level}
            onChange={setLevel}
          />

          <RadioGroup
            legend="დღიური მიზანი (წუთი)"
            orientation="horizontal"
            options={[
              { value: '5', label: '5' },
              { value: '10', label: '10' },
              { value: '15', label: '15' },
              { value: '20', label: '20' },
            ]}
            value={goal}
            onChange={setGoal}
          />
        </div>
      </section>

      {/* Toggles */}
      <section className="c1__section">
        <h2>Toggle</h2>
        <p className="c1__note">On / off switch. Used in settings for sound, haptics, notifications.</p>

        <div className="c1__stack">
          <Toggle label="ხმა გაკვეთილებზე" checked={sound} onChange={(e) => setSound(e.target.checked)} />
          <Toggle label="ვიბრაცია" checked={haptics} onChange={(e) => setHaptics(e.target.checked)} />
          <Toggle label="გამორთული" disabled />
        </div>
      </section>

      {/* Real form composition */}
      <section className="c1__section">
        <h2>Real form</h2>
        <p className="c1__note">All primitives together in a signup-style form. Submit runs the validators.</p>
        <div className="c1__form">
          <TextInput label="სახელი" placeholder="შენი სახელი" />
          <TextInput
            label="ელფოსტა"
            type="email"
            placeholder="you@mail.ge"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError}
          />
          <PasswordInput label="პაროლი" />
          <Checkbox
            label="ვეთანხმები წესებს"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <Button fullWidth size="lg" onClick={validateEmail} disabled={!agreed}>
            შექმენი ანგარიში
          </Button>
        </div>
      </section>

      <footer className="c1__footer">
        <p>
          <b>Cluster 1 review.</b> Tell Jake: focus ring feel, tap targets on phone, error states readable, placeholders
          clear, the three frames consistent.
        </p>
      </footer>
    </main>
  );
}
